import { useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ListFilter } from 'lucide-react';
import { BREAKPOINTS } from '@voll-intelligence/ui-kit/breakpoints';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  MapCentricStage,
  type MapCentricScrollDirection,
  type MapCentricStageFrame,
} from './map_centric_stage.js';
import { FiltersColumn } from './results_filters_column.js';
import { MapFab } from './map_fab.js';
import { RESULTS_LIST_SCROLLER_ATTR, ResultsListScrollContext } from './scroll_parent.js';

/**
 * O layout compartilhado das páginas de resultados (hotel, rent a car, voo —
 * #2994), na sequência fechada com o protótipo (Determinístico / Resultado):
 *
 * ```
 * [ Filtros · Redefinir ] [ título ─────────────── ações ] [ aside        ]
 * [ painel de cortes    ] [ meta ──── (Filtros) ordenador ] [ (mapa)       ]
 * [                     ] [ conteúdo                      ] [              ]
 * ```
 *
 * O que ele decide sozinho é só GEOMETRIA, medida na linha do corpo:
 * - a coluna de cortes (288px) nasce ABERTA e só sai da linha quando não sobra
 *   piso para o conteúdo — `sm` (640px) com o card sozinho ao lado dela, para a
 *   coluna encolher ANTES de o card trocar para o desenho de celular (o card de
 *   voo e o de carro quebram nessa régua); `MINIMUM_CONTENT_PX` com o mapa
 *   também na linha. Fora da linha ela vira o botão `Filtros` na linha da meta,
 *   que abre a folha do produto (`filtersSheet`);
 * - o aside (o mapa) fica ao lado enquanto cabe o piso `minMap`; sem caber vira
 *   o botão flutuante (`onAsideOpen`, o mapa em tela cheia do produto);
 * - a linha é uma página larga (`page-wide`, até 1280 com a margem), com ou
 *   sem aside; coluna, conteúdo e aside ficam a `COLUMN_GAP_PX` uns dos outros
 *   em todo produto (#4282, #4580).
 *
 * `narrow` (o palco estreito, `NARROW_STAGE_QUERY`) é do host: é a MESMA régua
 * do resto do produto e não uma leitura própria daqui. No estreito a coluna
 * está sempre fora e o aside sai da linha — para o botão (`narrowAside:
 * 'button'`, o padrão), ou para o PALCO (`'stage'`, #3106): o mapa toma a tela
 * e a página inteira (cabeçalho, meta, lista) vive numa folha de três paradas
 * por cima dele (`MapCentricStage`). Nesse modo a página TEM scroller próprio
 * (a folha), e o host precisa dar a ela uma altura travada.
 *
 * Não há rail recolhível, divisões Lista/Padrão/Mapa nem split arrastável
 * (decisão de #2994): o mapa toma o que sobra. Substituiu a casca antiga
 * (`ResultsPageShell`, CSS `iuk-results-page-*`), apagada com a última migração.
 *
 * Quem rola a lista é escolha do HOST (`listScroll`), porque só ele sabe se
 * dá à página uma altura travada:
 *
 * - `host` (o padrão): a página NÃO tem scroller próprio; quem rola é o host (o
 *   `.chat-scroll` do web-chat, o `.fex-results` do explorer no médio). Por
 *   isso a raiz e a linha têm altura NATURAL (nada de `min-h-0` encolhendo-as à
 *   viewport — o sticky abaixo mede a linha, e uma linha encolhida solta o pino
 *   no primeiro scroll), e as duas colunas fixas — a de cortes e o aside — são
 *   `sticky` com `self-start`. O `top` do pino é o PRÓPRIO `pt-6` da linha: o
 *   offset do sticky conta a partir da borda de conteúdo do scroller (depois do
 *   padding dele), então com o mesmo valor a coluna gruda exatamente onde
 *   nasce, sem pular. A altura desconta `--hui-results-page-chrome` — o que o
 *   host reserva acima da página no scroller (a barra em vidro do web-chat),
 *   lida com fallback para ele poder defini-la em QUALQUER ancestral; sem ela,
 *   a coluna cabe em `100dvh`. O preço: a barra da lista é a do host, na borda
 *   da janela (ou escondida, no `.chat-scroll`).
 * - `own` (#4587): o host trava a altura (uma cadeia `flex-1 min-h-0` até a
 *   raiz) e a página a ENCHE; quem rola é o `<main>` — título, meta e lista —,
 *   com a `scrollbar-rail` do DS sangrada para a direita: a barra cai no vão
 *   até o mapa, ou na margem da página sem ele, e a largura do conteúdo não
 *   muda. As colunas fixas esticam na linha (nada de `sticky` nem fórmula de
 *   `100dvh`), com o respiro do pé nelas; o da lista mora DENTRO do `<main>`,
 *   que rola até o pé do host. O `<main>` entra no Tab (PageDown, Espaço) e é
 *   nomeado pelo título. Só vale na linha: no palco estreito (`narrow`) quem
 *   rola segue sendo o host ou a folha do palco.
 *
 * Nos dois modos o botão flutuante do aside é `sticky` no pé do scroller, na
 * própria linha ao fim da lista: rolando, gruda a 2rem do pé (`MapFab`); no fim assenta
 * abaixo do último card, sem cobri-lo.
 *
 * A coluna de cortes é um PAINEL — heading e o corpo que rola — e termina 1.5rem
 * antes do pé do scroller, o mesmo respiro do topo; a lista respira o mesmo
 * `pb-6` no fim (na linha com `host`, no `<main>` com `own`). O `filtersFooter` (o composer do filtro
 * por texto) fica por cima do pé do corpo, sobre vidro: os eixos passam por
 * baixo e se dissolvem no desfoque, em vez de um corte seco (#4623). Ele mora
 * FORA do scroller (dentro, o recorte cortava a sombra do card) e o corpo
 * reserva no fim a altura medida dele, então o último eixo para inteiro acima.
 * A barra do corpo é a `scrollbar-rail` do DS, sangrada para o vão entre a
 * coluna e a lista (#4587).
 *
 * Tudo aqui é primitivo do DS + utilitária: nenhuma classe `iuk-*`.
 */
type ResultsLayoutCopy = {
  /** O heading da coluna e o botão que a substitui ("Filtros"). */
  filtersLabel: string;
  /** O gesto que zera o corte ("Redefinir"). */
  resetFiltersLabel: string;
  /** O botão flutuante que abre o aside em tela cheia ("Mapa"). */
  asideLabel?: string;
};

type ResultsLayoutFiltersSheetSlot = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** O botão `Filtros` que abriu — recebe o foco de volta ao fechar. */
  readonly returnFocusTo: RefObject<HTMLButtonElement | null>;
};

/** Onde o aside foi montado — o mapa muda de pele conforme o lugar. */
export type ResultsAsideFrame = MapCentricStageFrame & {
  /** `side`: a coluna ao lado da lista; `stage`: o palco inteiro, sob a folha. */
  readonly placement: 'side' | 'stage';
};

export type ResultsLayoutProps = {
  copy: ResultsLayoutCopy;
  /** O palco estreito (`NARROW_STAGE_QUERY`), lido pelo host. */
  narrow: boolean;
  /** O que o aside vira no palco estreito: o botão flutuante que abre o mapa
   *  cheio do produto (`button`, o padrão) ou o palco map-centric (`stage`). */
  narrowAside?: 'button' | 'stage';
  /** O h1 da página ("Hotéis em São Paulo"). */
  title: string;
  /** As ações à direita do título (compartilhar). */
  titleActions?: ReactNode;
  /** A meta ("8 de 214 resultados"), anunciada em `aria-live`. */
  meta: ReactNode;
  /** Uma faixa entre o título e a meta — a fila de trechos do voo (#2994). */
  banner?: ReactNode;
  /** Os controles à direita da meta (o ordenador); o botão `Filtros` entra
   *  ANTES deles quando a coluna está fora da linha. */
  metaActions?: ReactNode;
  /** `none`: a linha da meta fica só com a contagem — nem `Filtros`, nem o
   *  ordenador, nem a folha de cortes daqui — porque o produto os montou em
   *  outro lugar (a fila de chips na top bar do host, #3140). */
  metaControls?: 'inline' | 'none';
  /** Cortes ativos — o contador do botão `Filtros` e o que liga o `Redefinir`. */
  activeFilterCount: number;
  onResetFilters: () => void;
  /** O corpo da coluna de cortes (as seções); o heading é daqui. Ausente ⇒
   *  a página não tem cortes: sem coluna, sem botão `Filtros`, sem folha. */
  filters?: ReactNode;
  /** O rodapé fixo da coluna de cortes, por cima do pé do corpo que rola (o
   *  composer do filtro por texto). Só com `filters`; fora da linha o produto o
   *  monta na própria folha (`ResultsFiltersSheet.footer`). */
  filtersFooter?: ReactNode;
  /** A folha do produto para o corte fora da linha (`ResultsFiltersSheet`). */
  filtersSheet?: (slot: ResultsLayoutFiltersSheetSlot) => ReactNode;
  /** A coluna de conteúdo: lista e o "Ver mais" no pé. */
  content: ReactNode;
  /** O aside ao lado do conteúdo (o mapa). Ausente ⇒ linha centrada. Como
   *  função, recebe onde foi montado (`ResultsAsideFrame`) — o palco precisa
   *  de um mapa que enquadre acima da folha. */
  aside?: ReactNode | ((frame: ResultsAsideFrame) => ReactNode);
  /** Abre o aside em tela cheia quando ele não cabe ao lado. Sem ele o aside
   *  simplesmente não aparece fora da linha. */
  onAsideOpen?: () => void;
  /** Só no palco (`narrowAside: 'stage'`): a direção do scroll da lista na
   *  folha cheia, para o produto recolher o que montou fora dela — a fila de
   *  chips na top bar do host (#3274). Fora do palco quem rola é o host ou o
   *  `<main>` (`listScroll`). */
  onContentScrollDirection?: (direction: MapCentricScrollDirection) => void;
  /** Quem rola a lista na linha (ver o cabeçalho): `host` (o padrão) ou o
   *  próprio `<main>` (`own`), que exige do host uma altura travada. Ignorado
   *  no palco estreito. */
  listScroll?: 'host' | 'own';
};

/** A largura do painel de cortes sozinho, sem o vão até o conteúdo. 288 é o
 *  piso em que a grade 2×2 de Horário do voo ainda não quebra (#2351); a
 *  mesma coluna serve voo, hotel e carro. */
const FILTERS_COLUMN_PX = 288;
/** O piso do mapa ao lado: abaixo dele a página piora e ele vira botão. */
const MIN_ASIDE_PX = 352;
/** O piso do conteúdo COM o mapa ao lado: o card de hotel com nome e preço
 *  lado a lado. 448 e não 480 desde a margem de 32 da página larga: com ele a
 *  linha inteira (320 + 448 + 32 + 352 = 1152) cabe nos 1168 úteis de uma
 *  viewport de 1280 com a sidebar recolhida, com 16 de folga — o mapa cedeu
 *  32 do piso para os vãos de 32 (#4580). */
const MINIMUM_CONTENT_PX = 448;
/** O piso do conteúdo SEM o mapa, só com a coluna de cortes: a régua `sm`, a
 *  régua de componente do DS. Abaixo dela a coluna sai primeiro: o conteúdo
 *  sozinho não fica mais estreito que um palco largo. */
const CONTENT_ALONE_PX = BREAKPOINTS.sm;
/** O vão entre a coluna de cortes e o conteúdo, e entre o conteúdo e o aside:
 *  a página respira igual dos dois lados, em todo produto (#4282, #4580). */
const COLUMN_GAP_PX = 32;
/** A coluna de cortes com o vão até o conteúdo: o que ela tira da linha. */
const FILTERS_WIDTH_PX = FILTERS_COLUMN_PX + COLUMN_GAP_PX;

/** As classes da linha por quem rola a lista (`listScroll`, ver o cabeçalho). */
const LINE_CLASSES = {
  host: {
    section: 'flex min-w-0 flex-1 flex-col',
    // Piso de uma viewport: o sticky não sai do bloco que o contém, e com uma lista
    // mais curta que a coluna o bloco acabava antes dela e a empurrava para baixo da
    // barra do host ao rolar. Com o piso, a coluna (viewport − 3rem) sempre cabe.
    row: 'flex min-h-[calc(100dvh_-_var(--hui-results-page-chrome,0px))] page-wide flex-1 justify-center gap-4 pt-6 pb-6',
    inner: 'flex min-w-0 flex-1',
    main: 'flex min-w-0 flex-1 flex-col gap-3 pt-2',
    // O aside na MESMA régua do pino dos filtros; o `pt-2` alinha o topo do mapa
    // ao do título, e o 1rem a mais no desconto descola o pé dele do fim da viewport.
    aside:
      'sticky top-6 flex h-[calc(100dvh_-_var(--hui-results-page-chrome,0px)_-_2.5rem)] flex-1 flex-col self-start pt-2',
  },
  own: {
    section: 'flex min-h-0 min-w-0 flex-1 flex-col',
    row: 'flex min-h-0 page-wide flex-1 justify-center gap-4 pt-6',
    inner: 'flex min-h-0 min-w-0 flex-1',
    // A calha da barra SANGRA para a direita (`-mr-…`) e entra na base do flex
    // (`flex-[1_1_…]`): sem a base, a margem negativa viraria espaço livre
    // dividido com o mapa, e a lista perderia metade da calha. O `pb-6` é o
    // respiro do pé que no modo `host` é da linha — aqui a lista rola até o pé
    // do host e respira por dentro. O `px-1` é a calha do anel dos cards, pintado
    // FORA da caixa (1px, e o halo de 3px do selecionado): o `overflow-y: auto`
    // recorta o eixo X na padding box, e sem ela as laterais do anel sumiam
    // (#5506). As margens negativas a devolvem e a base do flex soma as duas
    // pontas — o conteúdo não sai do lugar nem muda de largura. À direita a
    // sangria para no `--page-gutter`: abaixo de `wide` a margem (16px) é só a
    // da barra, e passar dela transbordava a linha (rolagem lateral no host
    // `overflow-y-auto`); ali a calha sai do conteúdo. Com o aside a margem é 32.
    main: 'scrollbar-rail -ml-1 mr-[calc(-1*min(var(--scrollbar-rail)_+_0.25rem,var(--page-gutter)))] flex min-h-0 min-w-0 flex-[1_1_calc(0.5rem_+_var(--scrollbar-rail))] flex-col gap-3 overflow-y-auto px-1 pt-2 pb-6 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
    // Esticado na linha: o pé a 1rem do pé do host, como o do pino no `host`.
    aside: 'flex flex-1 flex-col pt-2 pb-4',
  },
} as const;

/** O piso do conteúdo com o aside, na caixa do `<main>`: com `own` a calha da
 *  barra e a do anel (`px-1`, as duas pontas) moram dentro dela e não podem
 *  sair dos 448 do card. */
const MAIN_MIN_WIDTH = {
  host: MINIMUM_CONTENT_PX,
  own: `calc(${String(MINIMUM_CONTENT_PX)}px + 0.5rem + var(--scrollbar-rail))`,
} as const;

/**
 * A régua da linha: mede o `clientWidth` do corpo (ResizeObserver) e responde
 * o que cabe. Nasce em `Infinity` — "não sei" é a apresentação larga, que é a
 * que não depende de medida — e só o primeiro `measure` a torna real.
 */
function useLineRuler(hasAside: boolean) {
  const [line, setLine] = useState<HTMLDivElement | null>(null);
  const [usable, setUsable] = useState(Infinity);
  useLayoutEffect(() => {
    if (!line) return undefined;
    // `clientWidth` INCLUI o padding, e o orçamento abaixo não: sem descontá-lo a
    // régua acha que cabem px que não existem. O padding é LIDO, não citado: a
    // margem da página (`--page-gutter`) é 16 ou 32 conforme a régua `wide`.
    const measure = () => {
      if (line.clientWidth === 0) return;
      const style = getComputedStyle(line);
      setUsable(
        line.clientWidth -
          (Number.parseFloat(style.paddingLeft) || 0) -
          (Number.parseFloat(style.paddingRight) || 0),
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(line);
    measure();
    return () => {
      observer.disconnect();
    };
  }, [line]);
  // O orçamento é a largura ÚTIL (`measure` já desconta o padding). Os três
  // encaixes juntos pedem 1152px (320 + 448 + 32 + 352); só a coluna e o
  // conteúdo, 960px (320 + 640).
  const asideFits = hasAside
    ? usable >= FILTERS_WIDTH_PX + MINIMUM_CONTENT_PX + COLUMN_GAP_PX + MIN_ASIDE_PX
    : false;
  // Com o aside ao lado, a coluna disputa a linha com ele; sem aside (ou com
  // ele já fora), só com o conteúdo.
  const filtersFit = asideFits
    ? usable >= FILTERS_WIDTH_PX + MINIMUM_CONTENT_PX + COLUMN_GAP_PX + MIN_ASIDE_PX
    : usable >= FILTERS_WIDTH_PX + CONTENT_ALONE_PX;
  return { lineRef: setLine, filtersFit, asideFits };
}

function OpenFiltersButton({
  label,
  active,
  buttonRef,
  onOpen,
}: {
  readonly label: string;
  readonly active: number;
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  readonly onOpen: () => void;
}): ReactNode {
  return (
    <Button ref={buttonRef} type="button" variant="outline" size="sm" onClick={onOpen}>
      <ListFilter className="size-4" aria-hidden="true" />
      <span>{label}</span>
      {active > 0 && (
        <span className="bg-foreground text-background inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] leading-none">
          {active}
        </span>
      )}
    </Button>
  );
}

function ResultsHeader({
  titleId,
  title,
  clampTitle,
  titleActions,
  meta,
  banner,
  openFilters,
  metaActions,
}: {
  readonly titleId: string;
  readonly title: string;
  /** Título em até duas linhas (o palco estreito) em vez de truncado. */
  readonly clampTitle: boolean;
  readonly titleActions: ReactNode;
  readonly meta: ReactNode;
  readonly banner: ReactNode;
  readonly openFilters: ReactNode;
  readonly metaActions: ReactNode;
}): ReactNode {
  const actions = openFilters || metaActions;
  return (
    <header className="flex flex-col gap-2.5">
      {/* No palco estreito o título ("Escolha a volta — GRU · São Paulo · Sáb,
          24 out") não cabe numa linha e truncado perde justamente a data: ali
          ele quebra em até duas linhas; no largo segue numa só. Na folha do
          palco map-centric volta a truncar: o espiar é uma altura fixa. */}
      <div className="flex min-h-8 items-center justify-between gap-3">
        <h1
          id={titleId}
          className={
            clampTitle
              ? 'm-0 line-clamp-2 min-w-0 text-xl leading-7 font-semibold tracking-[-0.01em]'
              : 'm-0 min-w-0 truncate text-xl leading-7 font-semibold tracking-[-0.01em]'
          }
          // Truncado numa linha, o título inteiro sobrevive no hover (#3608); o
          // texto completo já está no DOM para o leitor de tela.
          {...(clampTitle ? {} : { title })}
        >
          {title}
        </h1>
        {titleActions && <div className="flex shrink-0 items-center gap-2.5">{titleActions}</div>}
      </div>
      {banner}
      {/* A meta QUEBRA em vez de cortar: no celular o placar do roteiro do voo
          ("N voos · 1 de 2 escolhidos · Total parcial · R$ …") não cabe numa
          linha, e o corte comia justamente o total (#3844). */}
      <div className="flex min-h-8 items-center justify-between gap-3">
        <output
          aria-live="polite"
          className="text-muted-foreground min-w-0 text-[13px] leading-[18px]"
        >
          {meta}
        </output>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">
            {openFilters}
            {metaActions}
          </div>
        )}
      </div>
    </header>
  );
}

/** O botão flutuante do aside: só fora da linha, e só quando o host deu como
 *  abri-lo e como chamá-lo. */
function asideFab({
  show,
  label,
  onOpen,
}: {
  readonly show: boolean;
  readonly label: string | undefined;
  readonly onOpen: (() => void) | undefined;
}): ReactNode {
  if (!show || !label || !onOpen) return null;
  return <MapFab label={label} placement="sticky" onOpen={onOpen} />;
}

/** A folha só existe com a coluna FORA da linha; voltando para a linha ela se
 *  fecha, porque não tem mais o que cobrir. */
function useFiltersSheet(filtersInLine: boolean) {
  const [open, setOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  if (filtersInLine && open) setOpen(false);
  return { open, setOpen, openerRef };
}

/** A folha do produto só existe com a coluna FORA da linha e um `filtersSheet`
 *  para montá-la. */
function sheetOf(
  filtersOut: boolean,
  filtersSheet: ResultsLayoutProps['filtersSheet'],
  sheet: ReturnType<typeof useFiltersSheet>,
): ReactNode {
  if (!filtersOut || !filtersSheet) return null;
  return filtersSheet({
    open: sheet.open,
    onOpenChange: sheet.setOpen,
    returnFocusTo: sheet.openerRef,
  });
}

/** O quadro do aside AO LADO da lista: sem folha a cobrir nem a recolher. */
const SIDE_FRAME: ResultsAsideFrame = {
  placement: 'side',
  sheetHeight: 0,
  collapseSheet: () => undefined,
  showList: () => undefined,
};

/** O aside montado no lugar pedido: nó como veio, função com o quadro. */
function asideOf(aside: ResultsLayoutProps['aside'], frame: ResultsAsideFrame): ReactNode {
  return typeof aside === 'function' ? aside(frame) : aside;
}

/**
 * Onde cada peça cai nesta largura: a coluna de cortes na linha ou fora (o
 * botão `Filtros` + a folha), o aside ao lado, no botão ou no palco. A régua
 * da linha responde ao largo; `narrow` decide o estreito sozinho.
 */
function useResultsGeometry({
  narrow,
  narrowAside,
  hasFilters,
  hasAside,
}: {
  readonly narrow: boolean;
  readonly narrowAside: NonNullable<ResultsLayoutProps['narrowAside']>;
  readonly hasFilters: boolean;
  readonly hasAside: boolean;
}) {
  const { lineRef, filtersFit, asideFits } = useLineRuler(hasAside);
  const filtersInLine = hasFilters && !narrow && filtersFit;
  const asideInLine = !narrow && asideFits;
  const asideOnStage = narrow && hasAside && narrowAside === 'stage';
  return {
    lineRef,
    filtersInLine,
    filtersOut: hasFilters && !filtersInLine,
    asideInLine,
    asideOnStage,
    /** O botão flutuante: aside fora da linha e fora do palco. */
    asideOut: hasAside && !asideInLine && !asideOnStage,
    asideState: hasAside ? (asideInLine ? 'side' : 'out') : 'none',
  };
}

/** O botão `Filtros` da linha da meta — só com a coluna fora. */
function openFiltersOf(
  show: boolean,
  copy: ResultsLayoutCopy,
  active: number,
  sheet: ReturnType<typeof useFiltersSheet>,
): ReactNode {
  if (!show) return null;
  return (
    <OpenFiltersButton
      label={copy.filtersLabel}
      active={active}
      buttonRef={sheet.openerRef}
      onOpen={() => {
        sheet.setOpen(true);
      }}
    />
  );
}

/**
 * O palco map-centric: a mesma região e o mesmo cabeçalho, só que dentro da
 * folha; o mapa por trás. A folha de cortes fica IRMÃ da folha da página, não
 * filha — aninhada, o Base UI a trataria como gaveta empilhada (escala e
 * escurece a de baixo) e omitiria o véu dela.
 */
function StageSection({
  titleId,
  header,
  content,
  aside,
  showMapLabel,
  filtersSheet,
  onContentScrollDirection,
}: {
  readonly titleId: string;
  readonly header: ReactNode;
  readonly content: ReactNode;
  readonly aside: ResultsLayoutProps['aside'];
  readonly showMapLabel: string | undefined;
  readonly filtersSheet: ReactNode;
  readonly onContentScrollDirection: ResultsLayoutProps['onContentScrollDirection'];
}): ReactNode {
  return (
    <section
      aria-labelledby={titleId}
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      data-filters="out"
      data-aside="stage"
    >
      <MapCentricStage
        map={(frame) => asideOf(aside, { ...frame, placement: 'stage' })}
        header={header}
        content={content}
        labelledBy={titleId}
        copy={showMapLabel ? { showMapLabel } : {}}
        {...(onContentScrollDirection ? { onContentScrollDirection } : {})}
      />
      {filtersSheet}
    </section>
  );
}

export function ResultsLayout({
  copy,
  narrow,
  narrowAside = 'button',
  title,
  titleActions,
  meta,
  banner,
  metaActions,
  metaControls = 'inline',
  activeFilterCount,
  onResetFilters,
  filters,
  filtersFooter,
  filtersSheet,
  content,
  aside,
  onAsideOpen,
  onContentScrollDirection,
  listScroll = 'host',
}: ResultsLayoutProps): ReactNode {
  const hasAside = aside !== undefined;
  const geometry = useResultsGeometry({
    narrow,
    narrowAside,
    hasFilters: filters !== undefined,
    hasAside,
  });
  const { lineRef, filtersInLine, asideInLine, asideOnStage } = geometry;
  // Com os controles fora da linha (`none`), a coluna fora não vira botão nem folha.
  const filtersOut = geometry.filtersOut && metaControls === 'inline';
  const sheet = useFiltersSheet(filtersInLine);
  const fab = asideFab({ show: geometry.asideOut, label: copy.asideLabel, onOpen: onAsideOpen });

  // A página é UMA região nomeada pelo próprio título: quem navega por marco
  // acha a lista pelo nome da busca, e o h1 segue no outline.
  const titleId = useId();
  const header = (
    <ResultsHeader
      titleId={titleId}
      title={title}
      clampTitle={narrow && !asideOnStage}
      titleActions={titleActions}
      meta={meta}
      metaActions={metaControls === 'inline' ? metaActions : undefined}
      banner={banner}
      openFilters={openFiltersOf(filtersOut, copy, activeFilterCount, sheet)}
    />
  );
  if (asideOnStage) {
    return (
      <StageSection
        titleId={titleId}
        header={header}
        content={content}
        aside={aside}
        showMapLabel={copy.asideLabel}
        filtersSheet={sheetOf(filtersOut, filtersSheet, sheet)}
        onContentScrollDirection={onContentScrollDirection}
      />
    );
  }
  // `min-w-0` na raiz: o host a monta numa trilha `auto` de grid (a bancada do
  // hotel), e sem o mínimo zerado a trilha herda o min-content da página — a
  // faixa `nowrap` de comodidades do card fazia a coluna passar de 460px numa
  // tela de 390, com o ordenador saindo pela borda.
  const scroll = listScroll === 'own' && !narrow ? 'own' : 'host';
  const classes = LINE_CLASSES[scroll];
  return (
    <section
      aria-labelledby={titleId}
      className={classes.section}
      data-filters={filtersInLine ? 'open' : 'out'}
    >
      <div ref={lineRef} className={classes.row} data-aside={geometry.asideState}>
        <div className={classes.inner} style={{ gap: COLUMN_GAP_PX }}>
          {filtersInLine && (
            <FiltersColumn
              copy={copy}
              canReset={activeFilterCount > 0}
              onReset={onResetFilters}
              footer={filtersFooter}
              fill={scroll === 'own'}
            >
              {filters}
            </FiltersColumn>
          )}
          {/* Com o aside na linha, o conteúdo garante o próprio piso e o mapa fica
              com o que sobra (as duas colunas são `flex-1`): sem o piso, no
              limiar de 1152px úteis cada uma levaria 400px — abaixo dos 448 que a
              régua prometeu ao card. Fora da linha o piso sai, e a coluna encolhe com a
              tela. */}
          <main
            className={classes.main}
            style={asideInLine ? { minWidth: MAIN_MIN_WIDTH[scroll] } : undefined}
            {...(scroll === 'own'
              ? { tabIndex: 0, 'aria-labelledby': titleId, [RESULTS_LIST_SCROLLER_ATTR]: '' }
              : {})}
          >
            {header}
            {/* O contêiner `results-content` é a régua de COLUNA (não de janela) que
                um card pode consultar. Hoje nenhum o consulta: carro e ônibus trocam de
                desenho pelo palco (`NARROW_STAGE_QUERY`), como o hotel (#5125). */}
            <div className="@container/results-content flex flex-1 flex-col">
              <ResultsListScrollContext.Provider value={scroll}>
                {content}
              </ResultsListScrollContext.Provider>
            </div>
            {fab}
          </main>
          {asideInLine && <div className={classes.aside}>{asideOf(aside, SIDE_FRAME)}</div>}
        </div>
      </div>
      {sheetOf(filtersOut, filtersSheet, sheet)}
    </section>
  );
}
